import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';

import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
} from '@lexical/list';

import {  useCallback, useEffect, useRef, useState } from 'react';
import { INSERT_NUMBER_BOX_COMMAND } from './CasLexicalInputNumberPlugin';
import { FeatureConfig } from '../CasSimpleLexicalRichTextEditor';

const LowPriority = 1;

function Divider() {
  return <div className="divider" />;
}

type ToolbarProps = {
  features: FeatureConfig;
};

export default function ToolbarPlugin({ features }: ToolbarProps) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);

  const [toolbarState, setToolbarState] = useState({
    canUndo: false,
    canRedo: false,
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isStrikethrough: false,
    isOrderedList: false,
    isUnorderedList: false,
  });

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const topLevelElement = anchorNode.getTopLevelElement();
      const isList = topLevelElement && $isListNode(topLevelElement);

      const newState = {
        canUndo: toolbarState.canUndo,
        canRedo: toolbarState.canRedo,
        isBold: selection.hasFormat('bold'),
        isItalic: selection.hasFormat('italic'),
        isUnderline: selection.hasFormat('underline'),
        isStrikethrough: selection.hasFormat('strikethrough'),
        isOrderedList: isList ? topLevelElement.getListType() === 'number' : false,
        isUnorderedList: isList ? topLevelElement.getListType() === 'bullet' : false,
      };
      setToolbarState((prev) => ({ ...prev, ...newState }));
    }
  }, [toolbarState.canUndo, toolbarState.canRedo]);

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            $updateToolbar();
          });
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            $updateToolbar();
            return false;
          },
          LowPriority
        ),
        editor.registerCommand(
          CAN_UNDO_COMMAND,
          (payload) => {
            setToolbarState((prev) => ({ ...prev, canUndo: payload }));
            return false;
          },
          LowPriority
        ),
        editor.registerCommand(
          CAN_REDO_COMMAND,
          (payload) => {
            setToolbarState((prev) => ({ ...prev, canRedo: payload }));
            return false;
          },
          LowPriority
        )
      ),
    [editor, $updateToolbar]
  );

  return (
    <div className="toolbar" ref={toolbarRef}>
      {/* Undo/Redo */}
      {features.undoRedo && (
        <>
          <button
            type="button"
            disabled={!toolbarState.canUndo}
            onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
            className="toolbar-item spaced"
            aria-label="Undo"
          >
            <i className="format undo" />
          </button>
          <button
            type="button"
            disabled={!toolbarState.canRedo}
            onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
            className="toolbar-item"
            aria-label="Redo"
          >
            <i className="format redo" />
          </button>
          <Divider />
        </>
      )}

      {/* Text Formatting */}
      {features.bold && (
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          className={`toolbar-item spaced ${toolbarState.isBold ? 'active' : ''}`}
          aria-label="Format Bold"
        >
          <i className="format bold" />
        </button>
      )}
      {features.italic && (
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          className={`toolbar-item spaced ${toolbarState.isItalic ? 'active' : ''}`}
          aria-label="Format Italics"
        >
          <i className="format italic" />
        </button>
      )}
      {features.underline && (
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
          className={`toolbar-item spaced ${toolbarState.isUnderline ? 'active' : ''}`}
          aria-label="Format Underline"
        >
          <i className="format underline" />
        </button>
      )}
      {features.strikethrough && (
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
          className={`toolbar-item spaced ${toolbarState.isStrikethrough ? 'active' : ''}`}
          aria-label="Format Strikethrough"
        >
          <i className="format strikethrough" />
        </button>
      )}

      {/* Alignment */}
      {(features.alignLeft || features.alignCenter || features.alignRight || features.alignJustify) && (
        <>
          <Divider />
          {features.alignLeft && (
            <button
              type="button"
              onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
              className="toolbar-item spaced"
              aria-label="Left Align"
            >
              <i className="format left-align" />
            </button>
          )}
          {features.alignCenter && (
            <button
              type="button"
              onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
              className="toolbar-item spaced"
              aria-label="Center Align"
            >
              <i className="format center-align" />
            </button>
          )}
          {features.alignRight && (
            <button
              type="button"
              onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
              className="toolbar-item spaced"
              aria-label="Right Align"
            >
              <i className="format right-align" />
            </button>
          )}
          {features.alignJustify && (
            <button
              type="button"
              onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}
              className="toolbar-item spaced"
              aria-label="Justify Align"
            >
              <i className="format justify-align" />
            </button>
          )}
        </>
      )}

      {/* List Buttons */}
      {features.lists && (
        <>
          <Divider />
            <button
              type="button"
              className={`toolbar-item spaced ${toolbarState.isOrderedList ? 'active' : ''}`}
              aria-label="Ordered List"
              onClick={() => {
                if (toolbarState.isOrderedList) {
                  editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                } else {
                  editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
                }
              }}
            >
              <i className="format ordered-list" />
            </button>
            <button
              type="button"
              className={`toolbar-item spaced ${toolbarState.isUnorderedList ? 'active' : ''}`}
              aria-label="Unordered List"
              onClick={() => {
                if (toolbarState.isUnorderedList) {
                  editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                } else {
                  editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
                }
              }}
            >
              <i className="format unordered-list" />
            </button>
        </>
      )}

      {/* Custom Feature */}
      {features.numbers && (
        <button
          type="button"
          onClick={() => editor.dispatchCommand(INSERT_NUMBER_BOX_COMMAND, undefined)}
          className="toolbar-item"
        >
          <span>Insert</span>
        </button>
      )}
    </div>
  );
}
